import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { IFilterPageLayout } from '@ncg/data';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';
import { ProductsFacade } from '../../../+state/products/products.facade';
import { SettingsService } from '../../../core/settings.service';
import { ProductService } from '../../product.service';

@Component({
    selector: 'ncg-product-related',
    template: `
        <div class="grid__row grid__theme grid__theme--light section" *ngIf="hasRelatedProducts">
            <div class="container">
                <div class="headline-spot columns" ncgLoadIn>
                    <div class="spot-content column">
                        <div class="spot-content__header">
                            <div class="spot-content__header--title">
                                {{ 'product_details.related_cars_header' | translate }}
                            </div>
                            <div class="spot-content__header--subtitle">
                                {{ 'product_details.related_cars_subtitle' | translate }}
                            </div>
                        </div>
                    </div>
                </div>

                <div class="product-related" ncgLoadIn>
                    <ng-container #vc></ng-container>
                    <br />
                    <div class="product-related__actions has-text-centered">
                        <a *ngIf="usedCarsLink" [routerLink]="usedCarsLink" class="button is-primary">
                            {{ 'product_details.related_cars_link_text' | translate }}
                        </a>
                    </div>
                </div>
            </div>
        </div>
    `,
})
export class ProductRelatedComponent implements OnInit, OnDestroy {
    @Input() productId: string;
    @ViewChild('vc', { read: ViewContainerRef, static: false }) viewContainer: ViewContainerRef;
    hasRelatedProducts = true;
    usedCarsLink = '';

    private readonly unsubscribe = new Subject<void>();

    constructor(
        private readonly productService: ProductService,
        private readonly settingsService: SettingsService,
        private readonly productsFacade: ProductsFacade,
        private readonly cd: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        if (this.productId) {
            this._getRelated();
        }
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    private async _getRelated(): Promise<void> {
        const related$ = this.productService.getRelatedById(this.productId);
        const products = await firstValueFrom(related$);

        this.hasRelatedProducts = !!products.length;

        if (!this.hasRelatedProducts) {
            this.cd.markForCheck();
            return;
        }

        const { ProductListComponent } = await import('../../product-list/product-list.component');
        const componentRef = this.viewContainer.createComponent(ProductListComponent);
        componentRef.instance.items = products;
        componentRef.instance.shouldGetItems = false;
        componentRef.instance.layout = {
            view: 'list',
            pageLayout: await firstValueFrom(this._getLayout()),
        };
        componentRef.instance.ngOnChanges();
        this.cd.markForCheck();

        this._setLink();
    }

    private _setLink() {
        this.settingsService
            .get()
            .pipe(take(1), takeUntil(this.unsubscribe))
            .subscribe((settings) => {
                this.usedCarsLink = settings.usedCarsLink.url;
                this.cd.markForCheck();
            });
    }

    private _getLayout(): Observable<IFilterPageLayout> {
        return this.productsFacade.layout$.pipe(
            take(1),
            map((layout) => layout?.pageLayout || 'filterInSidepanel'),
            takeUntil(this.unsubscribe)
        );
    }
}
